import {
  HomeIcon,
  BeakerIcon,
  HeartIcon,
  FireIcon,
  CakeIcon,
  BookOpenIcon,
  DocumentTextIcon,
  UserIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  CameraIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline'

/**
 * The one navigation model — VH-133
 *
 * Three surfaces render this: the 256px sidebar, the 72px tablet rail and the
 * mobile bottom bar. They previously could not disagree because there was only
 * one of them; now that there are three, the structure lives here so a new
 * destination cannot appear in two of them and go missing from the third.
 *
 * The mockup showed a clean six-item sidebar. This app has fifteen
 * destinations, and dropping nine of them would have hidden working features
 * behind extra clicks — an information-architecture decision dressed up as a
 * reskin. Instead the sub-items become collapsible groups: the same visual
 * calm, nothing removed.
 *
 * VH-297 (HP-38): the phone surfaces read it too. `bottom` is an entry's place
 * in the phone's bottom bar (`phoneHref` where the phone opens another page),
 * `account` marks the entries that belong in the user menu's account part; the
 * menu lists everything else the bar has no room for.
 */

/** Items with `children` render as a collapsible group. */
export const getNavigation = (t, user) => {
  const nav = [
    // VH-247 stage 2: the first entry is the cycle home; the Dashboard is off the menu (still at /dashboard)
    { key: 'home', name: t('nav.home'), href: '/', icon: HomeIcon, bottom: 1 },
    {
      // VH-147: reported as "not available in light mode". The theme was a red
      // herring — there is no theme conditional here and never was. The real
      // difference was the device: on a phone the quick-log sheet lists this
      // directly, while on desktop it sat inside the collapsed Workouts group,
      // last, behind a chevron that gives no hint anything is under it.
      //
      // Promoted to the top level, directly under Dashboard, so the thing done
      // every training day is reachable from any route without expanding
      // anything. The copy inside Workouts stays (VH-138's ordering still
      // teaches the model) but is marked `shortcut` there, not here — this is
      // the row that owns the active state.
      key: 'logSessionTop',
      name: t('logSession.openWithoutQr'),
      href: '/log-session',
      icon: ClipboardDocumentCheckIcon,
    },
    {
      key: 'bloodTests',
      name: t('nav.bloodTests'),
      href: '/blood-tests',
      icon: BeakerIcon,
    },
    {
      key: 'supplements',
      name: t('nav.supplements'),
      href: '/supplements',
      icon: HeartIcon,
    },
    {
      key: 'workouts',
      name: t('nav.workouts'),
      href: '/workouts',
      icon: FireIcon,
      bottom: 2,
      // VH-136: ordered by how the concepts depend on each other, not
      // alphabetically or by age. A location owns equipment, equipment makes
      // exercises possible, exercises compose modules — so reading the menu
      // top to bottom teaches the model.
      children: [
        {
          key: 'trainingLocations',
          name: t('nav.trainingLocations'),
          href: '/training-locations',
          icon: MapPinIcon,
        },
        { key: 'exercises', name: t('nav.exercises'), href: '/exercises', icon: FireIcon },
        {
          key: 'templates',
          name: t('nav.templates'),
          href: '/templates',
          icon: RectangleGroupIcon,
        },
        {
          // VH-138: reachable from the menu as well as the quick-log sheet.
          // Quick-log is the mobile habit; the menu is where someone looks for
          // it deliberately.
          //
          // LAST on purpose (Reto's call). The first three entries are the
          // things you set up — location, exercises, modules — and reading them
          // in that order teaches the model. Reporting a session is what you do
          // with them afterwards, so it belongs after the setup, not inside it.
          //
          // VH-147: also promoted to a top-level entry, because on desktop this
          // row is invisible until the group is expanded and the daily action
          // should not be the hardest one to find. `shortcut` marks this copy so
          // it does not claim the active state — see isGroupActive.
          key: 'logSession',
          name: t('logSession.openWithoutQr'),
          href: '/log-session',
          icon: ClipboardDocumentCheckIcon,
          shortcut: true,
        },
      ],
    },
    {
      key: 'nutrition',
      name: t('nav.nutrition'),
      href: '/nutrition',
      icon: CakeIcon,
      bottom: 3,
      // VH-248: on a phone Nutrition opens the phone page; it links on to the full page.
      phoneHref: '/eat',
      children: [
        { key: 'recipes', name: t('nav.recipes'), href: '/recipes', icon: BookOpenIcon },
        // VH-253: also on the quick-log sheet, because the shop is not where
        // anyone expands a menu group. This copy is where it is LOOKED for.
        {
          key: 'shopping',
          name: t('nav.shopping'),
          href: '/shopping',
          icon: ShoppingCartIcon,
        },
      ],
    },
    {
      // VH-227 (user decision 2026-09-13): "Health Report" became "Health
      // Cycle" and is now the group; My Progress moved under it — the inverse
      // of before. /reports redirects to /health-cycle.
      key: 'healthCycle',
      name: t('nav.healthCycle'),
      href: '/health-cycle',
      icon: DocumentTextIcon,
      children: [
        {
          key: 'myProgress',
          name: t('nav.myProgress'),
          href: '/my-progress',
          icon: ChartBarIcon,
          bottom: 4,
        },
      ],
    },
    { key: 'profile', name: t('nav.profile'), href: '/profile', icon: UserIcon, account: true },
    {
      key: 'help',
      name: t('nav.help'),
      href: '/help',
      icon: QuestionMarkCircleIcon,
    },
  ]

  // VH-19: super_user only (replaced a hardcoded email check).
  if (user?.role === 'super_user') {
    nav.push({
      key: 'admin',
      name: t('nav.admin'),
      href: '/admin',
      icon: Cog6ToothIcon,
      account: true,
    })
  }

  return nav
}

const flatten = (items) => items.flatMap((item) => [item, ...(item.children ?? [])])

/**
 * The four destinations on the mobile bottom bar, either side of the action
 * button, in their `bottom` order. Chosen as the screens actually opened
 * daily. VH-297: read from getNavigation, so the bar and the user menu cannot
 * disagree (HP-38 rule 6); the bar links to `phoneHref` where an entry has one.
 */
export const getBottomNavigation = (t, user) =>
  flatten(getNavigation(t, user))
    .filter((item) => item.bottom)
    .sort((a, b) => a.bottom - b.bottom)

/**
 * VH-297 — what the phone's user menu lists before the account part: every
 * top-level destination that is not in the bottom bar, not an account entry
 * and not a second copy of a group's page (Log Session, promoted by VH-147, is
 * a Workouts tab on the phone). Children are left out: on the phone they are
 * their group's tabs.
 */
export const getMenuNavigation = (t, user) => {
  const nav = getNavigation(t, user)
  const groupPages = new Set(nav.flatMap((item) => (item.children ?? []).map((c) => c.href)))
  return nav
    .filter((item) => !item.bottom && !item.account && !groupPages.has(item.href))
    .map(({ children: _children, ...item }) => item)
}

/** VH-297 — the account entries of the user menu (Profile; Admin for super users). */
export const getAccountNavigation = (t, user) =>
  getNavigation(t, user).filter((item) => item.account)

/**
 * VH-297 — is the reader on this entry's page or below it? By address prefix,
 * so `/recipes/12` is still Recipes; the Dashboard (`/`) only on itself.
 */
export const isActivePath = (item, pathname) =>
  [item.href, item.phoneHref].filter(Boolean).some((href) =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)
  )

/**
 * VH-297 — does a bottom-bar entry own the current address? Its own page, or
 * one of its group's pages; a `shortcut` copy does not count (VH-147).
 */
export const ownsPath = (item, pathname) =>
  isActivePath(item, pathname) ||
  (item.children ?? []).some((child) => !child.shortcut && isActivePath(child, pathname))

/**
 * VH-297 — the phone's tabs for the address: the group whose page or sub-page
 * the reader is on, as its own page followed by its sub-pages, and the tab
 * that is current. Null outside a group.
 */
export const getGroupTabs = (t, user, pathname) => {
  for (const group of getNavigation(t, user)) {
    if (!group.children?.length) continue
    const current = group.children.find((child) => isActivePath(child, pathname))
    if (current || isActivePath(group, pathname)) {
      const { children, phoneHref, ...own } = group
      // A group with a phone page (Nutrition → /eat, VH-248) gets it as its
      // first tab, or the bottom bar's Nutrition lands on a page with no way
      // to Recipes or the shopping list (Reto 2026-10-24).
      const phone = phoneHref && { key: `${group.key}Phone`, name: t('eat.title'), href: phoneHref, icon: group.icon }
      const onPhonePage = phone && isActivePath(phone, pathname)
      return {
        group,
        tabs: phone ? [phone, own, ...children] : [own, ...children],
        current: (current ?? (onPhonePage ? phone : own)).key,
      }
    }
  }
  return null
}

/**
 * What the centre action button offers.
 *
 * Every entry routes to a screen that already exists — this is a shortcut, not
 * a new way to log anything. That boundary is deliberate: it keeps the most
 * visible piece of the redesign free of new create/update logic.
 */
export const getQuickLogActions = (t) => [
  { key: 'meal', name: t('quickLog.meal'), href: '/eat', icon: CakeIcon },
  {
    // VH-204: "Log workout" pointed at /workouts — the weekly-programme
    // planner, which is where you BUILD a week, not where you report one. On a
    // phone that reads as the desktop screen opening by mistake. The sheet is
    // the daily-habit surface, so the workout entry now goes where the habit
    // ends: the session report at /log-session.
    //
    // The separate "report a session" row that used to sit below is gone with
    // it — two rows to the same route in a five-item sheet is a choice with no
    // difference. /workouts is still one tap away in the bottom bar.
    key: 'workout',
    name: t('quickLog.workout'),
    href: '/log-session',
    icon: ClipboardDocumentCheckIcon,
  },
  {
    key: 'wellness',
    name: t('quickLog.wellness'),
    href: '/my-progress',
    icon: ChartBarIcon,
  },
  {
    // VH-253: one tap from any screen to the shopping list. The sheet is the
    // mobile habit surface and this is a phone-in-a-shop feature, so it belongs
    // here as much as in the menu.
    key: 'shopping',
    name: t('quickLog.shopping'),
    href: '/shopping',
    icon: ShoppingCartIcon,
  },
  {
    key: 'scan',
    name: t('quickLog.scan'),
    href: '/training-locations',
    icon: CameraIcon,
  },
]

/**
 * Does this item, or any of its children, own the current route?
 *
 * VH-147: a child marked `shortcut` is a second copy of a destination that
 * also exists at top level, kept inside the group for VH-138's teaching order.
 * It must NOT make its parent active, or `/log-session` lights up both the
 * promoted top-level row and the Workouts group, which reads as a rendering
 * fault rather than a location.
 */
export const isGroupActive = (item, pathname) =>
  item.href === pathname ||
  (item.children ?? []).some((child) => !child.shortcut && child.href === pathname)
